import React from 'react';
import styles from './TicketPreviewCard.module.css';
import { ticketSource } from '../semantic_search.types';
import { Config } from '../../core/config/config';
import { Helper } from '../../core/util/helper';
import { FAIcon } from '../../core/components/faIcon';

interface TicketPreviewCardProps {
    ticket: ticketSource;
    onClick?: () => void;
    icon: React.ReactNode;
    isFavorite: boolean;
    onToggleFavorite: () => void;
}

const parseDate = (value: Date | string) => {
    if (!value) return 'N/A';
    const date = Helper.dates.toDate(value);
    return kendo.toString(date, 'd');
};

const renderHighlightedText = (text: string | null | undefined) => {
    if (!text) {
        return null;
    }
    const parts = text.split(/(\|\|\|HIGHLIGHT\|\|\||\|\|\|ENDHIGHLIGHT\|\|\|)/g);
    return (
        <>
            {parts.map((part, index) => {
                if (part === '|||HIGHLIGHT|||') {
                    return <strong key={index} style={{ backgroundColor: "yellow" }}>{parts[index + 1]}</strong>;
                }
                if (part === '|||ENDHIGHLIGHT|||' || (index > 0 && parts[index-1] === '|||HIGHLIGHT|||')) {
                    return null;
                }
                return part;
            })}
        </>
    );
};

export const TicketPreviewCard: React.FC<TicketPreviewCardProps> = ({ ticket, onClick, icon, isFavorite, onToggleFavorite }) => {
    const hasComments = !Helper.isNullOrEmpty(ticket.FirstComment) || !Helper.isNullOrEmpty(ticket.LastComment);

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleFavorite();
    };

    return (
        <div className={styles.card} onClick={onClick}>
            <header className={styles.header}>
                <div className={styles.titleContainer}>
                    <FAIcon name="exclamation-square" prefix="fal" />
                    <div className={styles.titleAndNumber}>
                        <a
                            href={`${Config.baseUrl}Procedure/EditByGuid?isguid=${ticket.IsGuid}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={styles.titleLink}
                        >
                            {renderHighlightedText(ticket.Title)}
                        </a>
                        <div className={styles.ticketNumber}>
                            Ticket #{ticket.ProblemNr}
                        </div>
                    </div>
                </div>
                <div className={styles.metaIcons}>
                    <div className={styles.iconContainer}>
                        {icon}
                    </div>
                    <button onClick={handleFavoriteClick} className={styles.favoriteButton} title="Als Favorit markieren">
                        <FAIcon name="star" prefix={isFavorite ? 'fas' : 'fal'} />
                    </button>
                </div>
            </header>
            <div className={styles.content}>
                <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                        <FAIcon name={ticket.IsContainer ? "users" : "user"} prefix="fal" />
                        <span>{Helper.isNullOrEmpty(ticket.Hotliner) ? "n/a" : ticket.Hotliner}</span>
                    </div>
                    <div className={styles.detailItem}>
                        <FAIcon name="calendar" prefix="fal" />
                        <span>{parseDate(ticket.Deadline)}</span>
                    </div>
                    <div className={styles.detailItem}>
                        <FAIcon name="info-circle" prefix="fal" />
                        <span>{ticket.TicketType}</span>
                    </div>
                    <div className={styles.detailItem}>
                        <FAIcon name="heart-rate" prefix="fal" />
                        <span>{ticket.Status}</span>
                    </div>
                </div>
                {hasComments && <hr className={styles.separator} />}
                {hasComments && (
                    <div className={styles.commentsSection}>
                        {!Helper.isNullOrEmpty(ticket.FirstComment) && (
                            <div className={styles.comment}>
                                <FAIcon name="comment-alt" prefix="fal" />
                                <p>{renderHighlightedText(ticket.FirstComment)}</p>
                            </div>
                        )}
                        {(!Helper.isNullOrEmpty(ticket.LastComment) && ticket.FirstComment !== ticket.LastComment) && (
                            <div className={styles.comment}>
                                <FAIcon name="comment-alt" prefix="fal" />
                                <p>{renderHighlightedText(ticket.LastComment)}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};